<template>
    <div class="booker-profile">
        <section class="profile-bio">
            <div class="avatar-wrapper">
                <img :src="bookerInfo.avatar" alt="booker-avatar" class="avatar" />
            </div>
            <div class="bio-info">
                <div class="name-address">
                    <p class="name">{{ bookerName }}</p>
                    <p class="address">{{ bookerInfo.address }}</p>
                </div>
                <p class="bio">{{ bookerInfo.description }}</p>
                <button v-if="!currentUser && authed" @click="mailTo" class="message-booker">
                    Message
                </button>
                <button
                    v-if="currentUser && authed"
                    @click="editUserData()"
                    class="message-booker"
                >
                    Edit
                </button>
            </div>
        </section>
        <section v-if="artists.length" class="artist-pages">
            <p class="title">{{ bookerInfo.first_name }} Artist pages</p>
            <div class="artists">
                <Artist v-for="(artist, index) in artists" :key="index" :artist="artist" />
            </div>
        </section>
        <section v-if="venues.length" class="venue-pages">
            <p class="title">{{ bookerInfo.first_name }} Venue pages</p>
            <div class="venues">
                <Venue v-for="(user, index) in venues" :key="index" :user="user" />
            </div>
        </section>
        <section v-if="opportunities.length" class="my-opportunities">
            <p class="title">{{ bookerInfo.first_name }} Opportunities</p>
            <div class="opportunities">
                <base-card
                    v-for="(event, index) in opportunities"
                    :key="index.id"
                    :event="event"
                    :image="eventImage(index)"
                    :cta="{
                        text: 'Make an Offer',
                        disabled: false
                    }"
                    :styles="{
                        card: {
                            maxWidth: '336px'
                        }
                    }"
                    @cta-clicked="makeOffer(event.id)"
                >
                    <template #content-main>
                        <div class="opportunity-info">
                            <p class="date">{{ event.start_date }}</p>
                            <p class="venue-name">
                                {{
                                    event.venue_name
                                        ? event.venue_name
                                        : event.venue.venue_name
                                }}
                            </p>
                            <p class="venue-location">
                                {{
                                    event.city_with_state_address.replace(/[0-9]/g, '').trim()
                                }}
                            </p>
                        </div>
                        <p class="vibes">{{ eventVibes }}</p>
                    </template>
                    <template #footer-text>
                        <p class="label">Estimated payout</p>
                        <p
                            v-if="!event.venue_budget || event.venue_budget === '0.00'"
                            class="value"
                        >
                            N/A
                        </p>
                        <p v-else class="value">
                            Up to ${{ parseFloat(event.venue_budget).toFixed(0) }}
                        </p>
                    </template>
                </base-card>
            </div>
        </section>
        <section v-if="events.length" class="my-events">
            <p class="title">{{ bookerInfo.first_name }} Events</p>
            <div class="events">
                <base-card
                    v-for="(event, index) in events"
                    :key="index.id"
                    :event="event"
                    :image="eventImage(index)"
                    :cta="{
                        text: 'More Details',
                        disabled: false
                    }"
                    :styles="{
                        card: {
                            maxWidth: '336px',
                            maxHeight: '424px'
                        },
                        content: {
                            gap: '40px'
                        }
                    }"
                    @cta-clicked="redirectToEvent(event)"
                >
                    <template #content-main>
                        <div class="event-info">
                            <p class="date">{{ event.start_date }}</p>
                            <p class="venue-name">
                                {{
                                    event.venue_name
                                        ? event.venue_name
                                        : event.venue.venue_name
                                }}
                            </p>
                            <p class="venue-location">
                                {{
                                    event.city_with_state_address.replace(/[0-9]/g, '').trim()
                                }}
                            </p>
                        </div>
                        <div class="event-name-and-artists">
                            <p class="name">{{ event.event_name }}</p>
                            <p class="artists">{{ eventArtists(event) }}</p>
                        </div>
                        <div class="event-start-and-age">
                            <p class="start">
                                {{
                                    `Doors at ${event.start_time
                                        .split(' - ')[0]
                                        .replace(' ', '')}`
                                }}
                            </p>
                            <p v-if="event.min_age" class="age">{{ event.min_age }}+</p>
                            <p v-else class="age">All Ages</p>
                        </div>
                    </template>
                    <template #footer-text>
                        <p
                            v-if="event.cover_cost && event.cover_cost !== '0.00'"
                            class="value"
                        >
                            {{ `$${parseFloat(event.cover_cost).toFixed(0)} Cover` }}
                        </p>
                    </template>
                </base-card>
            </div>
        </section>
        <ArtistDirectBooking
            v-if="eventId"
            :authed="authed"
            :event-id="eventId"
            :user-id="userId"
            :step-id="bookingModalStep"
            :artist-pages="artists"
            :show-modal.sync="showBookingModal"
        />
    </div>
</template>
<script>
    import { api } from '@/helpers/api'
    import Artist from '@/components/search-result-artist/Artist.vue'
    import Venue from '@/components/search-result-venue/Venue.vue'
    import BaseCard from '@/components/ui/cards/BaseCard.vue'
    import ArtistDirectBooking from '@/components/artist-direct-booking/ArtistDirectBooking.vue'
    import axios from 'axios'

    const URLS = {
        xhr: {
            fetch: {
                booker: '/api/profile/v1/user-profile/booker-info',
                checkAuth: '/check-authorization',
                url: '/profile/v1/user-profile/settings'
            }
        }
    }

    export default {
        name: 'BookerProfile',
        components: {
            Artist,
            Venue,
            BaseCard,
            ArtistDirectBooking
        },
        props: {
            booker: {
                required: true
            },
            currentUser: {
                required: true
            }
        },
        data: () => ({
            eventId: null,
            authed: false,
            showBookingModal: false,
            bookingModalStep: null,
            load: false,
            bookerInfo: {}
        }),
        computed: {
            URLS() {
                return URLS
            },
            artists() {
                return this.bookerInfo.artistPages
            },
            venues() {
                return this.bookerInfo.venuePages
            },
            opportunities() {
                return this.bookerInfo.opportunities
            },
            events() {
                return this.bookerInfo.events
            },
            bookerName() {
                return `${this.bookerInfo.first_name}, ${
                    this.bookerInfo.last_name ? this.bookerInfo.last_name[0] : ''
                }`
            },
            userId() {
                return this.bookerInfo.user_id ?? null
            },
            eventImage: (self) => (event) => {
                if (event.image && !event.image.includes('/global/default.png')) {
                    return event.image
                } else if (event.venue?.avatar?.includes('/global/default.png')) {
                    return self.URLS.images.defaultEventImage
                } else {
                    return event.venue?.avatar
                }
            },
            eventArtists: () => (event) => {
                let artists = ''

                if (event.bookedArtists.length > 3) {
                    artists = event.bookedArtists
                        .slice(0, 3)
                        .map((a) => a.band_name)
                        .join(', ')

                    artists = `With ${artists} and ${event.bookedArtists.length - 3} more...`
                } else {
                    artists = event.bookedArtists.map((a) => a.band_name).join(', ')

                    artists = `With ${artists}`
                }

                return artists
            }
        },
        methods: {
            makeOffer(eventId) {
                this.eventId = eventId
                this.showBookingModal = true
            },
            async fetchBookerInfo() {
                try {
                    this.load = true
                    const data = await api({
                        method: 'get',
                        url: `${URLS.xhr.fetch.booker}/${this.booker}`
                    })
                    return data.data
                } finally {
                    this.load = false
                }
            },
            redirectToEvent(event) {
                window.open(`/event/show/${event.id}`, '_blank')
            },
            editUserData() {
                window.open(`${URLS.xhr.fetch.url}`, '_blank')
            },
            mailTo() {
                sessionStorage.setItem('recipient_id', this.userId)
                window.open('/profile/mail-chat', '_blank')
            },
            async checkAuth() {
                try {
                    const resp = await axios.get(URLS.xhr.fetch.checkAuth)

                    if (resp?.data?.isAuthorized) {
                        this.authed = true
                        return true
                    }
                    return false
                } catch {
                    this.authed = false
                    return false
                }
            }
        },
        async created() {
            await this.checkAuth()
            this.bookerInfo = await this.fetchBookerInfo()
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../../sass/_variables.scss';
    .date {
        color: #dedede;
        font-family: Instrument Sans;
        font-size: 20px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 500;
        line-height: 24px;
    }
    .venue-name {
        color: #dedede;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 500;
        line-height: 16px;
    }
    .venue-location {
        color: #dedede;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 500;
        line-height: 16px;
    }
    .vibes {
        color: #8b8b8b;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 400;
        line-height: 16px;
    }
    .footer-text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 48px;
        width: 100%;
    }
    .label {
        color: #8b8b8b;
        font-family: Instrument Sans;
        font-size: 12px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 400;
        line-height: 16px;
    }
    .value {
        color: #acacac;
        font-family: Instrument Sans;
        font-size: 16px;
        font-style: normal;
        font-variant-numeric: ordinal;
        font-weight: 500;
        line-height: 24px;
    }
    .name {
        overflow: hidden;
        color: #8b8b8b;
        font-variant-numeric: ordinal;
        text-overflow: ellipsis;
        @include base-font-14;
        font-weight: 600;
    }
    .artists {
        color: #8b8b8b;
        font-variant-numeric: ordinal;
        @include base-font-12;
        font-weight: 400;
        text-wrap: wrap;
    }
    .event-name-and-artists {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }
    .event-start-and-age {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 16px;

        .start,
        .age {
            color: #8b8b8b;
            @include base-font-14;
        }
    }
    .booker-profile {
        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        background: #121212;
        display: flex;
        width: 100%;
        max-width: 1168px;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        padding-left: 60px;
        padding-right: 60px;
        gap: 80px;
        margin-left: auto;
        margin-right: auto;

        @media screen and (max-width: 768px) {
            gap: 40px;
            padding-left: 16px;
            padding-right: 16px;
        }
        .profile-bio {
            width: 100%;
            padding-top: 23px;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 20px;

            @media screen and (max-width: 768px) {
                flex-direction: column;
                gap: 40px;
            }
            .avatar-wrapper {
                width: 274px;
                height: 274px;
                border-radius: 50%;

                .avatar {
                    width: 274px;
                    height: 274px;
                    border-radius: 50%;
                    object-fit: cover;
                }
            }
            .bio-info {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 20px;

                .name-address {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: flex-start;
                    gap: 0px;

                    .name {
                        color: #dedede;
                        font-family: Domine;
                        font-size: 32px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: 40px;
                    }
                    .address {
                        color: #8b8b8b;
                        @include base-font-16;
                        font-weight: 400;
                        line-height: 125%;
                    }
                }
                .bio {
                    color: #8b8b8b;
                    @include base-font-16;
                    font-weight: 400;
                    line-height: 24px;
                    white-space: pre-line;
                    max-width: 687px;
                    height: 144px;
                }
                .message-booker {
                    border-radius: 120px;
                    background: #cdeb09;
                    padding: 8px 12px;
                    color: #262626;
                    @include base-font-14;
                    line-height: 115%;
                    letter-spacing: 0.14px;
                }
            }
        }
        .artist-pages,
        .venue-pages,
        .my-opportunities,
        .my-events {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 24px;

            .title {
                color: #dedede;
                @include base-font-28;
                font-weight: 600;
                line-height: 32px;
            }
            .artists,
            .venues,
            .opportunities,
            .events {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 20px;
                flex-wrap: wrap;
                overflow-x: auto;
                padding-bottom: 10px;

                @media screen and (max-width: 768px) {
                    flex-wrap: nowrap;
                    overflow-x: auto;
                }
            }
            .artists::-webkit-scrollbar,
            .venues::-webkit-scrollbar,
            .opportunities::-webkit-scrollbar,
            .events::-webkit-scrollbar {
                height: 4px;
            }
            .artists::-webkit-scrollbar-track,
            .venues::-webkit-scrollbar-track,
            .opportunities::-webkit-scrollbar-track,
            .events::-webkit-scrollbar-track {
                background-color: none;
            }
            .artists::-webkit-scrollbar-thumb,
            .venues::-webkit-scrollbar-thumb,
            .opportunities::-webkit-scrollbar-thumb,
            .events::-webkit-scrollbar-thumb {
                max-height: 64px;
                border-radius: 8px;
                background: #8b8b8b;
            }
        }
    }
</style>
